import { Component, OnInit, ViewChild } from '@angular/core';
import { NavigationEnd, Router } from '@angular/router';
import {
	customersDefaultAuthRoles,
	customersPaths,
} from './customers/customers.routes';
import {
	dwrsDefaultAuthRoles,
	dwrsPaths,
} from './daily-work-reports/daily-work-reports.routes';
import {
	fieldTeamsDefaultAuthRoles,
	fieldTeamsPaths,
} from './field-teams/field-teams.routes';
import {
	filter,
	map,
	shareReplay,
	switchMap,
	takeUntil,
	tap,
} from 'rxjs/operators';
import {
	flashAuditsDefaultAuthRoles,
	flashAuditsPaths,
} from './flash-audits/flash-audits.routes';
import {
	invoicingDefaultAuthRoles,
	invoicingPaths,
} from './invoicing/invoicing.routes';
import { jobsDefaultAuthRoles, jobsPaths } from './jobs/jobs.routes';
import {
	jsfsDefaultAuthRoles,
	jsfsPaths,
} from './job-setup-forms/job-setup.forms.routes';
import {
	payrollDefaultAuthRoles,
	payrollPaths,
} from './payroll/payroll.routes.module';
import {
	personnelDefaultAuthRoles,
	personnelPaths,
} from './personnel/personnel.routes';
import { quotesDefaultAuthRoles, quotesPaths } from './quotes/quotes.routes';
import {
	railroadsDefaultAuthRoles,
	railroadsPaths,
} from './railroads/railroads.routes';
import { usersDefaultAuthRoles, usersPaths } from './users/users.routes';

import { AppState } from './shared/+state';
import { AuthService } from './shared/auth/auth.service';
import { Store } from '@ngrx/store';
import { AppInfoModel, UserClient } from 'src/shared/api';
import { VersionCheckerService } from './shared/version-checker.service';
import { appLaunched } from './shared/+state/app.actions';
import { environment } from 'src/environments/environment';
import {
	settingsDefaultAuthRoles,
	settingsPaths,
} from './settings/settings.routes';
import { AccountInfo } from '@azure/msal-browser';
import { MatSidenav } from '@angular/material/sidenav';
import { SelfDestruct } from 'src/shared/utilities/self-destruct';

export interface SidenavButton {
	title: string;
	link: string;
	allowedRoles: string[];
	icon: string;
	exact?: boolean;
}

// Google Analytics
declare let gtag: Function;

@Component({
	selector: 'rpfsa-root',
	templateUrl: './rpfsa.component.html',
	styleUrls: ['./rpfsa.component.scss'],
})
export class RpfsaComponent extends SelfDestruct implements OnInit {
	addContainer = false;
	roles: string[] = [];
	user: AccountInfo;

	isAuthenticated$ = this.authService.isAuthenticated$.pipe(shareReplay(1));

	sidenavAdminList: SidenavButton[] = [
		{
			title: 'Railroads',
			link: railroadsPaths.root,
			allowedRoles: railroadsDefaultAuthRoles,
			icon: 'train',
		},
		{
			title: 'Customers',
			link: customersPaths.root,
			allowedRoles: customersDefaultAuthRoles,
			icon: 'shopping_cart',
		},
		{
			title: 'Personnel',
			link: personnelPaths.root,
			allowedRoles: personnelDefaultAuthRoles,
			exact: true,
			icon: 'supervised_user_circle',
		},
		{
			title: 'Teams',
			link: fieldTeamsPaths.root,
			allowedRoles: fieldTeamsDefaultAuthRoles,
			icon: 'group',
		},
		{
			title: 'Users',
			link: usersPaths.root,
			allowedRoles: usersDefaultAuthRoles,
			icon: 'person',
		},
		{
			title: 'Payroll',
			link: payrollPaths.root,
			allowedRoles: payrollDefaultAuthRoles,
			icon: 'account_balance',
		},
		{
			title: 'Invoicing',
			link: invoicingPaths.root,
			allowedRoles: invoicingDefaultAuthRoles,
			icon: 'credit_card',
		},
		{
			title: 'Settings',
			link: settingsPaths.root,
			allowedRoles: settingsDefaultAuthRoles,
			icon: 'settings',
		},
	];
	sidenavOperationsList: SidenavButton[] = [
		{
			title: 'Quotes',
			link: quotesPaths.root,
			allowedRoles: quotesDefaultAuthRoles,
			icon: 'assignment',
		},
		{
			title: 'Jobs',
			link: jobsPaths.root,
			allowedRoles: jobsDefaultAuthRoles,
			icon: 'flag',
		},
		{
			title: 'Daily Work Reports',
			link: dwrsPaths.root,
			allowedRoles: dwrsDefaultAuthRoles,
			icon: 'access_time',
		},
		{
			title: 'Job Setup Forms',
			link: jsfsPaths.root,
			allowedRoles: jsfsDefaultAuthRoles,
			icon: 'assignment',
		},
		{
			title: 'Flash Audits',
			link: flashAuditsPaths.root,
			allowedRoles: flashAuditsDefaultAuthRoles,
			icon: 'flash_on',
		},
	];
	currentAppVersion: string;
	currentAppVersionFull: string;

	@ViewChild('sidenav') sidenav: MatSidenav;

	constructor(
		private router: Router,
		private authService: AuthService,
		private versionChecker: VersionCheckerService,
		private readonly store: Store<{ app: AppState }>,
		private readonly userClient: UserClient
	) {
		super();

		this.store.dispatch(appLaunched());
		this.versionChecker.getAppVersion().subscribe((x: AppInfoModel) => {
			this.currentAppVersion = x.versionNumber;
			this.currentAppVersionFull = x.versionNumberWithBuildNumber;
		});

		// Add the Google Tracking Tag to the page
		const script = document.createElement('script');
		script.async = true;
		script.src = `https://www.googletagmanager.com/gtag/js?id=${environment.googleAnalyticsCode}&l=gTagLayer`;
		document.head.prepend(script);

		// subscribe to router events and send page views to Google Analytics
		const navEndEvent$ = router.events.pipe(
			filter((e) => e instanceof NavigationEnd),
			map((e) => e as NavigationEnd)
		);

		navEndEvent$.subscribe((e: NavigationEnd) => {
			gtag('config', environment.googleAnalyticsCode, {
				page_path: e.urlAfterRedirects,
			});
		});
	}

	ngOnInit() {
		this.versionChecker.startPollingVersion();
		this.router.events.subscribe((evt) => {
			if (evt instanceof NavigationEnd) {
				this.addContainer = !evt.url.includes('assignments');
				window.scrollTo(0, 0);
			}
		});

		this.authService.isHandleRedirectCompleted$.subscribe(() =>
			this.authService.setActiveAccount()
		);

		this.authService.isAuthenticated$
			.pipe(
				takeUntil(this.destroyed),
				filter((val) => val),
				tap(() => {
					this.user = this.authService.msalUserInfo;
					this.roles = this.authService.userRoles;
				}),
				switchMap(() =>
					this.userClient.getAdminUserTeam(this.authService.userId)
				)
			)
			.subscribe((teamlink) => {
				this.authService.adminUserTeamLink = teamlink;
			});
	}

	sideNavToggle() {
		if (!this.sidenav) {
			return;
		}

		this.sidenav.toggle();
	}

	isAuthorizedRole(button: SidenavButton) {
		if (this.user) {
			if (!this.roles) {
				throw Error('Profile missing roles');
			}
			return button.allowedRoles.some((role) =>
				this.roles.includes(role)
			);
		}
		return false;
	}

	signOut(): void {
		this.authService.logout();
	}
}
